import { useMemo, useState } from 'react';
import { Archive as ArchiveIcon, Download, Search, ShieldCheck } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@solara/ui-shared';
import { useArchive } from '@solara/api-client/react';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';
import { saveCsv } from '../data/exports';

export default function ArchivePage() {
  const { t } = useTranslation('archive');
  const { locale } = useI18n();
  const { toast } = useToast();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  const [number, setNumber] = useState('');
  const [trn, setTrn] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [legal, setLegal] = useState<'all' | 'yes' | 'no'>('all');

  const archiveQuery = useArchive({ limit: 200 });
  // Page predates the DTO rename (`sealedAt`/`retainUntil` are the canonical
  // Prisma fields). Map them onto the legacy `createdAt`/`retentionUntil`
  // names this component still reads from.
  const allRecords = useMemo(
    () =>
      (archiveQuery.data ?? []).map((rec) => ({
        ...rec,
        createdAt: rec.sealedAt ?? new Date().toISOString(),
        retentionUntil: rec.retainUntil ?? new Date().toISOString(),
      })),
    [archiveQuery.data],
  );

  const results = useMemo(() => {
    return allRecords.filter((rec) => {
      if (number && !rec.id.toLowerCase().includes(number.toLowerCase())) return false;
      if (trn) return false; // backend archive records carry no TRN
      if (from && rec.createdAt.slice(0, 10) < from) return false;
      if (to && rec.createdAt.slice(0, 10) > to) return false;
      // amount filters are no-ops here — archive carries hashes, not totals.
      if (minAmount || maxAmount) return true;
      if (legal !== 'all') return true;
      return true;
    });
  }, [allRecords, number, trn, from, to, minAmount, maxAmount, legal]);

  function exportAll(fmt: 'csv' | 'pdf') {
    if (results.length === 0) return;
    if (fmt === 'csv') {
      const rows = results.map((rec) => {
        const inv = (rec as typeof rec & { invoice?: { totalMinor?: string; invoiceNumber?: string; buyerName?: string; sellerName?: string; issueDate?: string } }).invoice;
        const totalAed = inv?.totalMinor ? (Number(inv.totalMinor) / 100).toFixed(2) : '';
        return {
          archiveId: rec.id,
          invoiceNumber: inv?.invoiceNumber ?? '',
          seller: inv?.sellerName ?? '',
          buyer: inv?.buyerName ?? '',
          issueDate: inv?.issueDate?.slice(0, 10) ?? '',
          sealedAt: rec.createdAt.slice(0, 10),
          retentionUntil: rec.retentionUntil.slice(0, 10),
          totalAed,
          sha256: rec.sha256,
          storageKey: rec.storageKey,
        };
      });
      saveCsv(`solara-archive-${new Date().toISOString().slice(0, 10)}.csv`, rows, [
        'archiveId',
        'invoiceNumber',
        'seller',
        'buyer',
        'issueDate',
        'sealedAt',
        'retentionUntil',
        'totalAed',
        'sha256',
        'storageKey',
      ]);
      toast({ title: t('export.queued', { format: 'CSV' }), tone: 'success' });
      return;
    }
    // PDF would require server-side rendering — keep a toast for now.
    toast({
      title: t('export.queued', { format: fmt.toUpperCase() }),
      description: t('export.queuedBody', { count: results.length }),
      tone: 'info',
    });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="text-fg-muted text-sm">{t('subtitle')}</p>
      </header>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('search.title')}</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-3">
          <Input
            label={t('search.invoiceNumber')}
            placeholder="INV-2026-…"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            startIcon={<Search className="h-4 w-4" />}
          />
          <Input
            label={t('search.trn')}
            placeholder="100000000…"
            value={trn}
            onChange={(e) => setTrn(e.target.value.replace(/\s/g, ''))}
            inputMode="numeric"
            className="num"
            dir="ltr"
          />
          <Input
            label={t('search.minAmount')}
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            inputMode="decimal"
            className="num text-end"
            dir="ltr"
          />
          <Input
            label={t('search.maxAmount')}
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            inputMode="decimal"
            className="num text-end"
            dir="ltr"
          />
          <Input
            label={t('search.from')}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            dir="ltr"
          />
          <Input
            label={t('search.to')}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            dir="ltr"
          />
          <Select
            label={t('search.legalHold')}
            value={legal}
            onChange={(e) => setLegal(e.target.value as 'all' | 'yes' | 'no')}
            options={[
              { value: 'all', label: t('search.all') },
              { value: 'yes', label: t('search.yes') },
              { value: 'no', label: t('search.no') },
            ]}
          />
        </CardBody>
        <div className="px-6 pb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-fg-muted">
              {t('search.results', { count: results.length, total: allRecords.length })}
            </span>
            <span className="text-xs text-fg-subtle">{t('exportLimit')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => exportAll('csv')} className="gap-2">
              <Download className="h-4 w-4" /> {t('export.csv')}
            </Button>
            <Button variant="outline" onClick={() => exportAll('pdf')} className="gap-2">
              <Download className="h-4 w-4" /> {t('export.pdf')}
            </Button>
          </div>
        </div>
      </Card>

      {archiveQuery.isLoading ? (
        <LoadingBlock />
      ) : archiveQuery.error ? (
        <ErrorBanner error={archiveQuery.error} />
      ) : results.length === 0 ? (
        <EmptyState icon={<ArchiveIcon className="h-6 w-6" />} title={t('empty')} />
      ) : (
        <Card className="rounded-2xl">
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('table.invoice')}</th>
                    <th className="text-start font-medium pb-2">{t('table.issued')}</th>
                    <th className="text-end font-medium pb-2">{t('table.gross')}</th>
                    <th className="text-start font-medium pb-2">{t('table.retentionUntil')}</th>
                    <th className="text-start font-medium pb-2">{t('table.hashChain')}</th>
                    <th className="text-start font-medium pb-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {results.map((rec) => (
                    <tr key={rec.id}>
                      <td className="py-3">
                        <div className="font-medium font-mono text-xs">{rec.id.slice(0, 12)}…</div>
                        <div className="text-xs text-fg-subtle truncate max-w-[20ch]">
                          {rec.storageKey}
                        </div>
                      </td>
                      <td className="py-3 num">{dateFmt.format(new Date(rec.createdAt))}</td>
                      <td className="py-3 text-end num">
                        {aedFmt.format(
                          (() => {
                            const inv = (rec as typeof rec & { invoice?: { totalMinor?: string } }).invoice;
                            return inv?.totalMinor ? Number(inv.totalMinor) / 100 : 0;
                          })(),
                        )}
                      </td>
                      <td className="py-3 num">{dateFmt.format(new Date(rec.retentionUntil))}</td>
                      <td className="py-3">
                        <div className="flex items-center gap-1.5 text-xs">
                          <ShieldCheck className="h-3.5 w-3.5 text-success" />
                          <code className="text-[10px] truncate max-w-[14ch]">
                            {rec.sha256.slice(0, 10)}…
                          </code>
                        </div>
                      </td>
                      <td className="py-3">
                        <Button variant="ghost" size="sm" onClick={() => toast({ title: rec.id, tone: 'info' })}>
                          {t('table.open')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
